import { supabase } from '../lib/supabase';

export interface CronJobRow extends Record<string, unknown> {
  jobid: number;
  jobname: string;
  schedule: string;
  active: boolean;
  command: string;
  target_function: string;
  category: string;
  is_placeholder: boolean;
  database: string;
  username: string;
  last_run_at: string | null;
  last_status: string | null;
  run_days: number[];
  run_hour_utc: number | null;
  run_minute_utc: number;
  schedule_kind: 'daily' | 'hourly' | 'every_n_hours';
  every_n_hours: number | null;
  max_batch: number | null;
  supports_max_batch: boolean;
  /** False when cron_job_settings row is missing — UI should parse pg_cron schedule instead of defaults. */
  has_settings?: boolean;
}

/** Derive Control Panel fields from a pg_cron five-field expression. */
export function parseCronSchedule(expr: string): Pick<
  CronJobRow,
  'run_days' | 'run_hour_utc' | 'run_minute_utc' | 'schedule_kind' | 'every_n_hours'
> {
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) {
    return {
      run_days: [0, 1, 2, 3, 4, 5, 6],
      run_hour_utc: null,
      run_minute_utc: 0,
      schedule_kind: 'daily',
      every_n_hours: null,
    };
  }

  const minute = parts[0];
  const hour = parts[1];
  const dow = parts[4];

  const parseDow = (field: string): number[] => {
    if (field === '*') return [0, 1, 2, 3, 4, 5, 6];
    const days = new Set<number>();
    for (const chunk of field.split(',')) {
      if (chunk.includes('-')) {
        const [a, b] = chunk.split('-').map((n) => parseInt(n, 10));
        if (Number.isFinite(a) && Number.isFinite(b)) {
          for (let d = a; d <= b; d++) days.add(d);
        }
      } else {
        const d = parseInt(chunk, 10);
        if (Number.isFinite(d)) days.add(d);
      }
    }
    return days.size ? [...days].sort((a, b) => a - b) : [0, 1, 2, 3, 4, 5, 6];
  };

  const runDays = parseDow(dow);
  const runMinute = Math.min(59, Math.max(0, parseInt(minute, 10) || 0));

  if (hour.startsWith('*/')) {
    const n = parseInt(hour.slice(2), 10);
    return {
      run_days: runDays,
      run_hour_utc: null,
      run_minute_utc: runMinute,
      schedule_kind: 'every_n_hours',
      every_n_hours: Number.isFinite(n) ? n : null,
    };
  }

  if (hour === '*') {
    return {
      run_days: runDays,
      run_hour_utc: null,
      run_minute_utc: runMinute,
      schedule_kind: 'hourly',
      every_n_hours: null,
    };
  }

  const runHour = parseInt(hour, 10);
  return {
    run_days: runDays,
    run_hour_utc: Number.isFinite(runHour) ? runHour : null,
    run_minute_utc: runMinute,
    schedule_kind: 'daily',
    every_n_hours: null,
  };
}

/** When settings row is missing, show the live pg_cron schedule in the Control Panel. */
export function reconcileCronJobRow(job: CronJobRow): CronJobRow {
  if (job.has_settings !== false) return job;
  const parsed = parseCronSchedule(job.schedule);
  return { ...job, ...parsed, has_settings: false };
}

export interface CronJobRunRow extends Record<string, unknown> {
  runid: number;
  jobid: number;
  jobname: string | null;
  status: string;
  return_message: string | null;
  start_time: string;
  end_time: string | null;
  duration_ms: number | null;
  command: string;
}

function mapRpcError(error: { message?: string; code?: string } | null): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501' || error.message?.includes('forbidden')) {
    return 'Access denied — owner admin only.';
  }
  return error.message ?? 'Request failed';
}

/** Jobs that must exist in cron.job after migrations 20260609120036+. */
export const EXPECTED_CRON_JOBS = [
  'email-funnel-onboarding-nudge',
  'email-funnel-weekly',
  'email-funnel-optimization-reminder',
  'email-apply-reminder',
  'daily-tip-email-hourly',
  'send-job-digest-daily',
  'peerlist-ingest-every-6h',
  'outreach-batch-daily',
  'compute-kpis-daily',
  'generate-blog-draft-weekdays',
  'blog-draft-stale-reminder-daily',
] as const;

export interface CronEmailRunRow extends Record<string, unknown> {
  id: number;
  job_name: string;
  email_type: string | null;
  ok: boolean;
  sent_count: number;
  skipped_count: number;
  error_message: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export interface IngestionLogRow extends Record<string, unknown> {
  log_id: number;
  source: string;
  ok: boolean;
  seen: number;
  created_count: number;
  updated_count: number;
  failed: number;
  duration_ms: number;
  error: string | null;
  created_at: string;
}

export async function fetchIngestionLogs(limit = 100): Promise<IngestionLogRow[]> {
  const { data, error } = await supabase.rpc('admin_list_ingestion_logs', { p_limit: limit });
  if (error) {
    const msg = mapRpcError(error);
    if (msg.includes('admin_list_ingestion_logs') || msg.includes('does not exist')) {
      throw new Error(
        'Ingestion history RPC missing — run `supabase db push` (migration 20260604100024).',
      );
    }
    throw new Error(msg);
  }
  return (data ?? []) as IngestionLogRow[];
}

export async function invokePeerlistIngest(): Promise<number> {
  const { data, error } = await supabase.rpc('admin_invoke_peerlist_ingest');
  if (error) throw new Error(mapRpcError(error));
  return typeof data === 'number' ? data : 0;
}

export async function fetchCronEmailRuns(limit = 100): Promise<CronEmailRunRow[]> {
  const { data, error } = await supabase.rpc('admin_list_cron_email_runs', { p_limit: limit });
  if (error) {
    const msg = mapRpcError(error);
    if (msg.includes('admin_list_cron_email_runs') || msg.includes('does not exist')) {
      return [];
    }
    throw new Error(msg);
  }
  return (data ?? []) as CronEmailRunRow[];
}

/** pg_cron runs + ingestion + email cron logs + manual edge runs in one timeline. */
export async function fetchMergedExecutionHistory(limit = 200): Promise<CronJobRunRow[]> {
  const quarter = Math.ceil(limit / 4);
  const [cronRuns, ingestLogs, emailRuns, edgeRuns] = await Promise.all([
    fetchCronJobRuns({ limit: quarter }),
    fetchIngestionLogs(quarter).catch((e: Error) => {
      console.warn('[cron] ingestion_logs unavailable:', e.message);
      return [] as IngestionLogRow[];
    }),
    fetchCronEmailRuns(quarter).catch((e: Error) => {
      console.warn('[cron] cron_email_runs unavailable:', e.message);
      return [] as CronEmailRunRow[];
    }),
    fetchCronEdgeRuns(quarter).catch((e: Error) => {
      console.warn('[cron] cron_edge_run_log unavailable:', e.message);
      return [] as CronEdgeRunRow[];
    }),
  ]);

  const ingestAsRuns: CronJobRunRow[] = ingestLogs.map((row) => {
    const endMs = new Date(row.created_at).getTime() + (row.duration_ms ?? 0);
    const msg = `seen=${row.seen} created=${row.created_count} updated=${row.updated_count} failed=${row.failed}${row.error ? ` · ${row.error}` : ''}`;
    return {
      runid: 1_000_000_000 + row.log_id,
      jobid: -1,
      jobname: `${row.source}-ingest`,
      status: row.ok ? 'succeeded' : 'failed',
      return_message: msg,
      start_time: row.created_at,
      end_time: new Date(endMs).toISOString(),
      duration_ms: row.duration_ms,
      command: 'edge function (ingestion_logs)',
    };
  });

  const emailAsRuns: CronJobRunRow[] = emailRuns.map((row) => {
    const msg = row.ok
      ? `sent=${row.sent_count} skipped=${row.skipped_count}${row.email_type ? ` · ${row.email_type}` : ''}`
      : (row.error_message ?? `no sends (sent=${row.sent_count})`);
    return {
      runid: 2_000_000_000 + row.id,
      jobid: -2,
      jobname: row.job_name,
      status: row.ok && row.sent_count > 0 ? 'succeeded' : (row.sent_count === 0 ? 'skipped' : 'failed'),
      return_message: msg,
      start_time: row.created_at,
      end_time: row.created_at,
      duration_ms: null,
      command: 'edge function (cron_email_runs)',
    };
  });

  const edgeAsRuns: CronJobRunRow[] = edgeRuns.map((row) => {
    const start = row.created_at;
    const end = row.completed_at ?? null;
    const pending = end == null;
    const status = pending ? 'running' : (row.ok ? 'succeeded' : 'failed');
    const msg = pending
      ? `manual trigger — awaiting response (request #${row.request_id ?? '—'})`
      : (row.ok
          ? `HTTP ${row.http_status ?? '—'}${row.response_preview ? ` · ${row.response_preview.slice(0, 120)}` : ''}`
          : (row.error_message
              ?? `HTTP ${row.http_status ?? '—'}${row.response_preview ? ` · ${row.response_preview.slice(0, 120)}` : ''}`));
    return {
      runid: 3_000_000_000 + row.id,
      jobid: -3,
      jobname: row.jobname,
      status,
      return_message: msg,
      start_time: start,
      end_time: end,
      duration_ms: row.duration_ms,
      command: `manual invoke → ${row.edge_function ?? 'edge function'}`,
    };
  });

  return [...cronRuns, ...ingestAsRuns, ...emailAsRuns, ...edgeAsRuns]
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
    .slice(0, limit);
}

export async function fetchCronJobs(): Promise<CronJobRow[]> {
  const { data, error } = await supabase.rpc('admin_list_cron_jobs');
  if (error) throw new Error(mapRpcError(error));
  return ((data ?? []) as CronJobRow[]).map(reconcileCronJobRow);
}

export async function fetchCronJobRuns(opts?: {
  limit?: number;
  jobId?: number;
}): Promise<CronJobRunRow[]> {
  const { data, error } = await supabase.rpc('admin_list_cron_job_runs', {
    p_limit: opts?.limit ?? 150,
    p_jobid: opts?.jobId ?? null,
  });
  if (error) throw new Error(mapRpcError(error));
  return (data ?? []) as CronJobRunRow[];
}

export async function updateCronJobSettings(
  jobname: string,
  patch: {
    run_days: number[];
    run_hour_utc?: number | null;
    run_minute_utc?: number;
    schedule_kind?: 'daily' | 'hourly' | 'every_n_hours';
    every_n_hours?: number | null;
    max_batch?: number | null;
    active?: boolean;
  },
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_update_cron_job_settings', {
    p_jobname: jobname,
    p_run_days: patch.run_days,
    p_run_hour_utc: patch.run_hour_utc ?? null,
    p_run_minute_utc: patch.run_minute_utc ?? 0,
    p_schedule_kind: patch.schedule_kind ?? 'daily',
    p_every_n_hours: patch.every_n_hours ?? null,
    p_max_batch: patch.max_batch ?? null,
    p_active: patch.active ?? null,
  });
  if (error) throw new Error(mapRpcError(error));
  return typeof data === 'string' ? data : '';
}

export async function updateCronJob(
  jobId: number,
  patch: { schedule?: string; active?: boolean },
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_cron_job', {
    p_jobid: jobId,
    p_schedule: patch.schedule ?? null,
    p_active: patch.active ?? null,
  });
  if (error) throw new Error(mapRpcError(error));
}

export async function runCronJob(jobId: number): Promise<number> {
  const { data, error } = await supabase.rpc('admin_run_cron_job', { p_jobid: jobId });
  if (error) throw new Error(mapRpcError(error));
  return typeof data === 'number' ? data : 0;
}

/** Parse pg_net request id from RPC (may arrive as string for large bigint values). */
function parsePgNetRequestId(data: unknown): number {
  if (typeof data === 'number' && Number.isFinite(data)) return data;
  if (typeof data === 'string' && data.trim()) {
    const n = Number(data);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Stable string form for poll RPC (avoids JSON bigint / PostgREST signature issues). */
function pgNetRequestIdParam(requestId: number): string {
  return String(requestId);
}

/** Directly invoke a cron job's edge function via pg_net (faster, returns real request_id). */
export async function invokeCronJobDirect(jobname: string): Promise<number> {
  const { data, error } = await supabase.rpc('admin_invoke_cron_job', { p_jobname: jobname });
  if (error) throw new Error(mapRpcError(error));
  return parsePgNetRequestId(data);
}

/** One row of the persistent manual/polled run log (public.cron_edge_run_log). */
export interface CronEdgeRunRow extends Record<string, unknown> {
  id: number;
  jobname: string;
  request_id: number | null;
  edge_function: string | null;
  ok: boolean;
  http_status: number | null;
  response_preview: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  duration_ms: number | null;
}

/** Result of polling a Run-Now invocation by its pg_net request id. */
export interface CronInvokePoll {
  /** True once the edge function has responded (or errored). */
  done: boolean;
  /** Status is in the 2xx range and there was no transport error. */
  ok: boolean;
  /** Coarse phase while not done: 'queued' | 'invalid_request_id' | undefined. */
  phase: string | null;
  /** HTTP status returned by the edge function. */
  httpStatus: number | null;
  /** First ~800 chars of the response body (often JSON with sent/created counts). */
  responsePreview: string | null;
  /** Transport-level error (DNS, TLS, timeout) if any. */
  errorMessage: string | null;
}

/**
 * Record a pending manual run BEFORE the edge function responds, so Execution
 * History shows a "running" row immediately. Best-effort: never throws.
 */
export async function beginCronInvoke(
  requestId: number,
  jobname: string,
  edgeFunction?: string | null,
): Promise<void> {
  if (!requestId || requestId <= 0) return;
  try {
    await supabase.rpc('admin_begin_cron_invoke', {
      p_request_id: requestId,
      p_jobname: jobname,
      p_edge_fn: edgeFunction ?? null,
    });
  } catch {
    // Logging is non-critical — the poll below still upserts the final outcome.
  }
}

/**
 * Poll the async result of a Run-Now invocation. While the edge function is
 * still running this returns `{ done: false, phase: 'queued' }`. When it
 * responds, the backend also persists the outcome to cron_edge_run_log so it
 * appears in Execution History. Returns `{ done: false }` on any RPC error so
 * the caller falls back to its timeout path (never a hard failure).
 */
export async function pollCronInvoke(
  requestId: number,
  jobname?: string,
): Promise<CronInvokePoll> {
  const pending: CronInvokePoll = {
    done: false,
    ok: false,
    phase: 'queued',
    httpStatus: null,
    responsePreview: null,
    errorMessage: null,
  };
  if (!requestId || requestId <= 0) return pending;

  const { data, error } = await supabase.rpc('admin_poll_cron_invoke', {
    p_request_id: pgNetRequestIdParam(requestId),
    p_jobname: jobname ?? null,
  });
  if (error) {
    const msg = error.message ?? '';
    const code = error.code ?? '';
    const rpcMissing =
      code === 'PGRST202' ||
      code === '42883' ||
      /could not find the function public\.admin_poll_cron_invoke/i.test(msg);
    if (rpcMissing) {
      // Invoke already succeeded — polling is best-effort only.
      return {
        ...pending,
        done: true,
        ok: true,
        phase: 'poll_unavailable',
      };
    }
    if (code === '42501' || /forbidden/i.test(msg)) {
      return {
        ...pending,
        done: true,
        ok: true,
        phase: 'poll_access_denied',
      };
    }
    return pending;
  }

  const row = (data ?? {}) as Record<string, unknown>;
  return {
    done: row.done === true,
    ok: row.ok === true,
    phase: typeof row.phase === 'string' ? row.phase : null,
    httpStatus: typeof row.http_status === 'number' ? row.http_status : null,
    responsePreview: typeof row.response_preview === 'string' ? row.response_preview : null,
    errorMessage: typeof row.error_message === 'string' ? row.error_message : null,
  };
}

export async function fetchCronEdgeRuns(limit = 100): Promise<CronEdgeRunRow[]> {
  const { data, error } = await supabase.rpc('admin_list_cron_edge_runs', { p_limit: limit });
  if (error) {
    const msg = mapRpcError(error);
    if (msg.includes('admin_list_cron_edge_runs') || msg.includes('does not exist')) {
      return [];
    }
    throw new Error(msg);
  }
  return (data ?? []) as CronEdgeRunRow[];
}

/** Per-job hint shown after Run Now so the user knows where to see the result. */
export function runNowResultHint(jobname: string): string {
  if (jobname.includes('blog-draft-stale-reminder')) {
    return 'Check bell tray for stale draft alerts, or Admin → Blog posts.';
  }
  if (jobname.includes('generate-blog-draft') || jobname.includes('blog')) {
    return 'Draft will appear in Admin → Blog posts in ~30 s. Refresh that page.';
  }
  if (jobname.includes('peerlist-ingest')) {
    return 'Check Execution History or public.ingestion_logs in ~2 min.';
  }
  if (jobname.includes('job-digest')) {
    return 'Digest emails queued — check Execution History → cron_email_runs for sent count.';
  }
  if (jobname.includes('outreach')) {
    return 'Outreach batch queued — check Execution History in ~30 s.';
  }
  if (jobname.includes('compute-kpis')) {
    return 'KPI snapshot will appear in public.kpi_snapshots in ~30 s.';
  }
  if (jobname.includes('email-funnel') || jobname.includes('daily-tip') || jobname.includes('apply-reminder')) {
    return 'Emails queued — check Execution History for sent/skipped count.';
  }
  return 'Check Execution History in ~30 s to see the result.';
}

/** Human-readable cron schedule hint (best-effort). */
/** pg_cron DOW: 0=Sun … 6=Sat (matches cron_job_settings.run_days). */
export const CRON_WEEKDAYS: { value: number; label: string; short: string }[] = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

export function formatRunDays(runDays: number[] | null | undefined): string {
  if (!runDays?.length) return 'Every day';
  return runDays
    .slice()
    .sort((a, b) => a - b)
    .map((d) => CRON_WEEKDAYS.find((w) => w.value === d)?.short ?? String(d))
    .join(', ');
}

export function describeCronSchedule(expr: string, job?: CronJobRow): string {
  const e = expr.trim();
  const presets: Record<string, string> = {
    '0 * * * *': 'Every hour',
    '0 9 * * *': 'Daily at 09:00 UTC',
    '30 9 * * *': 'Daily at 09:30 UTC',
    '0 10 * * 2,5': 'Tue & Fri at 10:00 UTC',
    '0 10 * * 3': 'Wednesdays at 10:00 UTC',
    '30 18 * * *': 'Daily at 18:30 UTC',
    '30 2 * * *': 'Daily at 02:30 UTC (~08:00 IST)',
    '30 2 * * 1': 'Mondays at 02:30 UTC',
    '0 8 * * *': 'Daily at 08:00 UTC',
    '30 6 * * 1-5': 'Weekdays at 06:30 UTC',
    '15 */6 * * *': 'Every 6 hours (:15 past)',
  };
  return presets[e] ?? (job?.run_days?.length ? `${formatRunDays(job.run_days)} · ${e}` : e);
}

export function statusChipClass(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'succeeded' || s === 'success') return 'bg-emerald-100 text-emerald-800';
  if (s === 'skipped') return 'bg-amber-100 text-amber-800';
  if (s === 'running' || s === 'started') return 'bg-blue-100 text-blue-800';
  if (s === 'failed' || s === 'error') return 'bg-red-100 text-red-800';
  return 'bg-stone-100 text-stone-700';
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
